import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  LayoutDashboard, Library, FolderTree, Sparkles, FileText, Upload, LogOut,
  Plus, Trash2, Pencil, X, Check, Eye, Download, Loader2, ShieldCheck,
  Settings as SettingsIcon, Image as ImageIcon, UserCog
} from "lucide-react";
import { api, API, clearToken, getToken } from "@/library/lib/api";
import { toast } from "sonner";
import PdfViewerModal from "@/library/components/PdfViewerModal";
import { resolveLogoUrl } from "@/library/lib/settings";

const tabs = [
  { key: "overview", label: "نظرة عامة", icon: LayoutDashboard },
  { key: "settings", label: "الإعدادات", icon: SettingsIcon },
  { key: "levels", label: "المراحل", icon: Library },
  { key: "domains", label: "المجالات", icon: FolderTree },
  { key: "indicators", label: "المؤشرات", icon: Sparkles },
  { key: "files", label: "الملفات", icon: FileText },
];

const AdminDashboard = () => {
  const nav = useNavigate();
  const [tab, setTab] = useState("overview");
  const [stats, setStats] = useState({ files: 0, indicators: 0, domains: 0, levels: 0, users: 0 });
  const [levels, setLevels] = useState([]);
  const [domains, setDomains] = useState([]);
  const [indicators, setIndicators] = useState([]);
  const [files, setFiles] = useState([]);
  const [viewFileId, setViewFileId] = useState(null);

  const refresh = async () => {
    const [s, l, d, i, f] = await Promise.all([
      api.get("/stats"), api.get("/levels"), api.get("/domains"), api.get("/indicators"), api.get("/files")
    ]);
    setStats(s.data); setLevels(l.data); setDomains(d.data); setIndicators(i.data); setFiles(f.data);
  };

  useEffect(() => {
    if (!getToken()) { nav("/library/admin/login"); return; }
    api.get("/auth/me").catch(() => nav("/library/admin/login"));
    refresh();
  }, []);

  const logout = () => { clearToken(); nav("/"); };

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-[#F5F7FA]">
      {/* Topbar */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-30">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-brand-dark to-brand flex items-center justify-center">
              <ShieldCheck className="w-5 h-5 text-white" />
            </div>
            <div>
              <div className="font-heading font-extrabold text-brand-dark text-sm">لوحة إدارة المكتبة</div>
              <div className="text-[11px] text-slate-500">مدارس العلم النافع</div>
            </div>
          </div>
          <button onClick={logout} data-testid="admin-logout" className="flex items-center gap-2 text-sm text-slate-600 hover:text-red-600 font-semibold">
            <LogOut className="w-4 h-4" /> خروج
          </button>
        </div>
      </header>

      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 py-6 grid lg:grid-cols-[260px_1fr] gap-6">
        {/* Sidebar */}
        <aside className="bg-white rounded-2xl p-3 shadow-sm border border-slate-100 h-fit lg:sticky lg:top-20">
          <nav className="space-y-1">
            {tabs.map((t) => (
              <button
                key={t.key}
                data-testid={`tab-${t.key}`}
                onClick={() => setTab(t.key)}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-semibold transition-colors ${
                  tab === t.key ? "bg-brand text-white shadow" : "text-slate-700 hover:bg-slate-100"
                }`}
              >
                <t.icon className="w-4 h-4" /> {t.label}
              </button>
            ))}
            <div className="pt-2 mt-2 border-t border-slate-100">
              <button
                data-testid="tab-staff-admin"
                onClick={() => nav("/library/admin/staff")}
                className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-semibold transition-colors text-slate-700 hover:bg-gold/10 hover:text-gold-dark"
              >
                <UserCog className="w-4 h-4" /> منسوبي المدرسة
              </button>
            </div>
          </nav>
        </aside>

        {/* Content */}
        <main>
          {tab === "overview" && <Overview stats={stats} />}
          {tab === "settings" && <SettingsManager />}
          {tab === "levels" && <LevelsManager levels={levels} refresh={refresh} />}
          {tab === "domains" && <DomainsManager levels={levels} domains={domains} refresh={refresh} />}
          {tab === "indicators" && <IndicatorsManager domains={domains} indicators={indicators} levels={levels} refresh={refresh} />}
          {tab === "files" && <FilesManager files={files} indicators={indicators} domains={domains} levels={levels} refresh={refresh} onView={setViewFileId} />}
        </main>
      </div>
      <PdfViewerModal fileId={viewFileId} onClose={() => setViewFileId(null)} />
    </div>
  );
};

const StatBox = ({ label, value, icon: Icon, color }) => (
  <div className="bg-white rounded-2xl p-5 border border-slate-100 shadow-sm">
    <div className="flex items-center justify-between">
      <div>
        <div className="text-xs text-slate-500 font-bold">{label}</div>
        <div className="font-heading text-3xl font-extrabold text-brand-dark tabular-nums mt-1">{value}</div>
      </div>
      <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${color}`}>
        <Icon className="w-6 h-6" />
      </div>
    </div>
  </div>
);

const Overview = ({ stats }) => (
  <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="space-y-6">
    <div>
      <h2 className="font-heading text-2xl font-extrabold text-brand-dark">نظرة عامة</h2>
      <p className="text-sm text-slate-500 mt-1">ملخص شامل لمحتوى المكتبة الرقمية.</p>
    </div>
    <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
      <StatBox label="المراحل" value={stats.levels} icon={Library} color="bg-blue-50 text-brand" />
      <StatBox label="المجالات" value={stats.domains} icon={FolderTree} color="bg-emerald-50 text-emerald-700" />
      <StatBox label="المؤشرات" value={stats.indicators} icon={Sparkles} color="bg-amber-50 text-gold-dark" />
      <StatBox label="الملفات" value={stats.files} icon={FileText} color="bg-rose-50 text-rose-600" />
      <StatBox label="المستخدمون" value={stats.users} icon={ShieldCheck} color="bg-violet-50 text-violet-700" />
    </div>
    <div className="bg-white rounded-2xl p-8 border border-slate-100 shadow-sm">
      <h3 className="font-heading font-bold text-brand-dark text-lg mb-3">إرشادات سريعة</h3>
      <ol className="text-sm text-slate-600 leading-loose list-decimal ps-5 space-y-1">
        <li>أضف <b>المجالات</b> من تبويب المجالات وربطها بالمراحل المناسبة.</li>
        <li>أضف <b>المؤشرات</b> داخل كل مجال.</li>
        <li>ارفع <b>ملفات PDF</b> من تبويب الملفات واربطها بالمؤشر المناسب.</li>
      </ol>
    </div>
  </motion.div>
);

const Card = ({ children, title, action }) => (
  <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
    <div className="flex items-center justify-between p-5 border-b border-slate-100">
      <h3 className="font-heading font-bold text-brand-dark">{title}</h3>
      {action}
    </div>
    <div className="p-5">{children}</div>
  </div>
);

const Btn = ({ children, color = "brand", ...rest }) => {
  const map = {
    brand: "bg-brand text-white hover:bg-brand-light",
    gold: "bg-gold text-white hover:bg-gold-light",
    red: "bg-red-50 text-red-600 hover:bg-red-100",
    ghost: "bg-slate-100 text-slate-700 hover:bg-slate-200",
  };
  return <button {...rest} className={`px-3 py-2 rounded-lg text-sm font-semibold transition-colors disabled:opacity-50 ${map[color]} ${rest.className || ""}`}>{children}</button>;
};

// ===== Levels =====
const LevelsManager = ({ levels, refresh }) => {
  const [name, setName] = useState(""); const [desc, setDesc] = useState("");
  const [editing, setEditing] = useState(null);

  const save = async () => {
    if (!name.trim()) return toast.error("الاسم مطلوب");
    try {
      if (editing) await api.put(`/levels/${editing}`, { name, description: desc, order: 0, icon: "graduation-cap" });
      else await api.post("/levels", { name, description: desc, order: levels.length + 1, icon: "graduation-cap" });
      toast.success("تم الحفظ"); setName(""); setDesc(""); setEditing(null); refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || "فشل الحفظ"); }
  };
  const del = async (id) => {
    if (!window.confirm("حذف المرحلة وكل ما تحتها؟")) return;
    try {
      await api.delete(`/levels/${id}`); toast.success("تم الحذف"); refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || "فشل الحذف"); }
  };

  return (
    <Card title="إدارة المراحل">
      <div className="grid md:grid-cols-3 gap-3 mb-4">
        <input data-testid="level-name-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="اسم المرحلة" className="px-4 py-2.5 rounded-lg border border-slate-200 text-sm" />
        <input data-testid="level-desc-input" value={desc} onChange={(e) => setDesc(e.target.value)} placeholder="وصف مختصر (اختياري)" className="px-4 py-2.5 rounded-lg border border-slate-200 text-sm md:col-span-1" />
        <div className="flex gap-2">
          <Btn data-testid="level-save-btn" onClick={save}><Plus className="w-4 h-4 inline ms-1" />{editing ? "تحديث" : "إضافة"}</Btn>
          {editing && <Btn color="ghost" onClick={() => { setEditing(null); setName(""); setDesc(""); }}><X className="w-4 h-4" /></Btn>}
        </div>
      </div>
      <div className="space-y-2">
        {levels.map((l) => (
          <div key={l.id} data-testid={`admin-level-${l.id}`} className="flex items-center justify-between p-3 rounded-xl border border-slate-100 hover:border-brand/30 transition-colors">
            <div className="min-w-0">
              <div className="font-bold text-brand-dark text-sm">{l.name}</div>
              <div className="text-xs text-slate-500 truncate">{l.description}</div>
            </div>
            <div className="flex gap-2 shrink-0">
              <Btn color="ghost" onClick={() => { setEditing(l.id); setName(l.name); setDesc(l.description || ""); }} data-testid={`level-edit-${l.id}`}><Pencil className="w-3.5 h-3.5" /></Btn>
              <Btn color="red" onClick={() => del(l.id)} data-testid={`level-del-${l.id}`}><Trash2 className="w-3.5 h-3.5" /></Btn>
            </div>
          </div>
        ))}
        {levels.length === 0 && <div className="text-center py-8 text-slate-400 text-sm">لا توجد مراحل</div>}
      </div>
    </Card>
  );
};

// ===== Domains =====
const DomainsManager = ({ levels, domains, refresh }) => {
  const [name, setName] = useState(""); const [desc, setDesc] = useState(""); const [levelId, setLevelId] = useState("");
  const [editing, setEditing] = useState(null);
  const save = async () => {
    if (!name.trim() || !levelId) return toast.error("الاسم والمرحلة مطلوبان");
    try {
      const payload = { name, description: desc, level_id: levelId, order: 0, icon: "book-open" };
      if (editing) await api.put(`/domains/${editing}`, payload);
      else await api.post("/domains", payload);
      toast.success("تم الحفظ"); setName(""); setDesc(""); setEditing(null); refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || "فشل حفظ المجال"); }
  };
  const del = async (id) => {
    if (!window.confirm("حذف المجال؟")) return;
    try {
      await api.delete(`/domains/${id}`); toast.success("تم الحذف"); refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || "فشل الحذف"); }
  };

  return (
    <Card title="إدارة المجالات">
      <div className="grid md:grid-cols-4 gap-3 mb-4">
        <select data-testid="domain-level-select" value={levelId} onChange={(e) => setLevelId(e.target.value)} className="px-4 py-2.5 rounded-lg border border-slate-200 text-sm bg-white">
          <option value="">اختر المرحلة</option>
          {levels.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
        </select>
        <input data-testid="domain-name-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="اسم المجال" className="px-4 py-2.5 rounded-lg border border-slate-200 text-sm" />
        <input data-testid="domain-desc-input" value={desc} onChange={(e) => setDesc(e.target.value)} placeholder="وصف (اختياري)" className="px-4 py-2.5 rounded-lg border border-slate-200 text-sm" />
        <div className="flex gap-2">
          <Btn data-testid="domain-save-btn" onClick={save}><Plus className="w-4 h-4 inline ms-1" /> {editing ? "تحديث" : "إضافة"}</Btn>
          {editing && <Btn color="ghost" onClick={() => { setEditing(null); setName(""); setDesc(""); }}><X className="w-4 h-4" /></Btn>}
        </div>
      </div>
      <div className="space-y-2">
        {domains.map((d) => (
          <div key={d.id} data-testid={`admin-domain-${d.id}`} className="flex items-center justify-between p-3 rounded-xl border border-slate-100">
            <div className="min-w-0">
              <div className="font-bold text-brand-dark text-sm">{d.name}</div>
              <div className="text-xs text-slate-500 truncate">{levels.find((l) => l.id === d.level_id)?.name} • {d.description}</div>
            </div>
            <div className="flex gap-2 shrink-0">
              <Btn color="ghost" onClick={() => { setEditing(d.id); setName(d.name); setDesc(d.description || ""); setLevelId(d.level_id); }} data-testid={`domain-edit-${d.id}`}><Pencil className="w-3.5 h-3.5" /></Btn>
              <Btn color="red" onClick={() => del(d.id)} data-testid={`domain-del-${d.id}`}><Trash2 className="w-3.5 h-3.5" /></Btn>
            </div>
          </div>
        ))}
        {domains.length === 0 && <div className="text-center py-8 text-slate-400 text-sm">لا توجد مجالات</div>}
      </div>
    </Card>
  );
};

// ===== Indicators =====
const IndicatorsManager = ({ domains, indicators, levels, refresh }) => {
  const [name, setName] = useState(""); const [desc, setDesc] = useState(""); const [domainId, setDomainId] = useState("");
  const [editing, setEditing] = useState(null);
  const save = async () => {
    if (!name.trim() || !domainId) return toast.error("الاسم والمجال مطلوبان");
    try {
      const payload = { name, description: desc, domain_id: domainId, order: 0 };
      if (editing) await api.put(`/indicators/${editing}`, payload);
      else await api.post("/indicators", payload);
      toast.success("تم الحفظ"); setName(""); setDesc(""); setEditing(null); refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || "فشل حفظ المؤشر"); }
  };
  const del = async (id) => {
    if (!window.confirm("حذف المؤشر؟")) return;
    try {
      await api.delete(`/indicators/${id}`); toast.success("تم الحذف"); refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || "فشل الحذف"); }
  };
  return (
    <Card title="إدارة المؤشرات">
      <div className="grid md:grid-cols-4 gap-3 mb-4">
        <select data-testid="indicator-domain-select" value={domainId} onChange={(e) => setDomainId(e.target.value)} className="px-4 py-2.5 rounded-lg border border-slate-200 text-sm bg-white">
          <option value="">اختر المجال</option>
          {domains.map((d) => <option key={d.id} value={d.id}>{d.name} ({levels.find((l) => l.id === d.level_id)?.name})</option>)}
        </select>
        <input data-testid="indicator-name-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="اسم المؤشر" className="px-4 py-2.5 rounded-lg border border-slate-200 text-sm" />
        <input data-testid="indicator-desc-input" value={desc} onChange={(e) => setDesc(e.target.value)} placeholder="وصف (اختياري)" className="px-4 py-2.5 rounded-lg border border-slate-200 text-sm" />
        <div className="flex gap-2">
          <Btn data-testid="indicator-save-btn" onClick={save}><Plus className="w-4 h-4 inline ms-1" /> {editing ? "تحديث" : "إضافة"}</Btn>
          {editing && <Btn color="ghost" onClick={() => { setEditing(null); setName(""); setDesc(""); }}><X className="w-4 h-4" /></Btn>}
        </div>
      </div>
      <div className="space-y-2 max-h-[500px] overflow-y-auto">
        {indicators.map((ind) => {
          const dom = domains.find((d) => d.id === ind.domain_id);
          return (
            <div key={ind.id} data-testid={`admin-indicator-${ind.id}`} className="flex items-center justify-between p-3 rounded-xl border border-slate-100">
              <div className="min-w-0">
                <div className="font-bold text-brand-dark text-sm">{ind.name}</div>
                <div className="text-xs text-slate-500 truncate">{dom?.name}</div>
              </div>
              <div className="flex gap-2 shrink-0">
                <Btn color="ghost" onClick={() => { setEditing(ind.id); setName(ind.name); setDesc(ind.description || ""); setDomainId(ind.domain_id); }} data-testid={`indicator-edit-${ind.id}`}><Pencil className="w-3.5 h-3.5" /></Btn>
                <Btn color="red" onClick={() => del(ind.id)} data-testid={`indicator-del-${ind.id}`}><Trash2 className="w-3.5 h-3.5" /></Btn>
              </div>
            </div>
          );
        })}
        {indicators.length === 0 && <div className="text-center py-8 text-slate-400 text-sm">لا توجد مؤشرات</div>}
      </div>
    </Card>
  );
};

// ===== Settings =====
const SettingsManager = () => {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    api.get("/settings").then((r) => setS(r.data)).catch(() => {});
  }, []);

  const upd = (k, v) => setS({ ...s, [k]: v });

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/settings", s);
      setS(data);
      toast.success("تم حفظ الإعدادات. حدّث الصفحة لرؤية التغييرات في باقي المواقع.");
    } catch (e) {
      toast.error("فشل الحفظ");
    } finally { setSaving(false); }
  };

  const uploadLogo = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/settings/logo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setS({ ...s, logo_url: data.logo_url });
      toast.success("تم رفع الشعار");
    } catch (err) {
      toast.error("فشل رفع الشعار");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  if (!s) return <div className="text-center py-12 text-slate-400">جارٍ التحميل...</div>;

  const fld = (label, k, textarea = false, type = "text") => (
    <div>
      <label className="block text-xs font-bold text-slate-600 mb-1.5">{label}</label>
      {textarea ? (
        <textarea data-testid={`settings-${k}`} value={s[k] || ""} onChange={(e) => upd(k, e.target.value)} rows={3} className="w-full px-4 py-2.5 rounded-lg border border-slate-200 text-sm focus:border-brand focus:ring-2 focus:ring-brand/15 outline-none" />
      ) : (
        <input data-testid={`settings-${k}`} type={type} value={s[k] || ""} onChange={(e) => upd(k, e.target.value)} className="w-full px-4 py-2.5 rounded-lg border border-slate-200 text-sm focus:border-brand focus:ring-2 focus:ring-brand/15 outline-none" />
      )}
    </div>
  );

  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="space-y-6">
      <div>
        <h2 className="font-heading text-2xl font-extrabold text-brand-dark">إعدادات الموقع</h2>
        <p className="text-sm text-slate-500 mt-1">عدّل اسم المدرسة والنصوص الترحيبية والشعار. تنطبق التغييرات على كامل الموقع.</p>
      </div>

      <Card title="شعار المدرسة">
        <div className="flex flex-col sm:flex-row items-start gap-6">
          <div className="w-32 h-32 rounded-2xl border-2 border-dashed border-slate-200 bg-slate-50 flex items-center justify-center overflow-hidden shrink-0">
            {s.logo_url ? (
              <img src={resolveLogoUrl(s.logo_url)} alt="logo" className="w-full h-full object-contain" data-testid="settings-logo-preview" />
            ) : (
              <ImageIcon className="w-10 h-10 text-slate-300" />
            )}
          </div>
          <div className="flex-1 space-y-3 w-full">
            <input ref={fileRef} type="file" accept="image/*" hidden onChange={uploadLogo} data-testid="settings-logo-input" />
            <Btn color="brand" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="settings-logo-upload">
              {uploading ? <><Loader2 className="w-4 h-4 inline animate-spin ms-1" /> جارٍ الرفع...</> : <><Upload className="w-4 h-4 inline ms-1" /> رفع شعار جديد</>}
            </Btn>
            <div>
              <label className="block text-xs font-bold text-slate-600 mb-1.5">أو ألصق رابط الشعار (URL)</label>
              <input data-testid="settings-logo_url" type="text" value={s.logo_url || ""} onChange={(e) => upd("logo_url", e.target.value)} placeholder="https://..." className="w-full px-4 py-2.5 rounded-lg border border-slate-200 text-sm" dir="ltr" />
            </div>
          </div>
        </div>
      </Card>

      <Card title="الهوية المؤسسية">
        <div className="grid md:grid-cols-2 gap-4">
          {fld("اسم النظام / الموقع", "site_name")}
          {fld("اسم المدرسة", "school_name")}
        </div>
      </Card>

      <Card title="القسم الترحيبي (Hero)">
        <div className="grid md:grid-cols-2 gap-4">
          {fld("العنوان الرئيسي", "hero_title")}
          {fld("العنوان الفرعي", "hero_subtitle")}
        </div>
        <div className="mt-4">
          {fld("شارة أعلى العنوان (Badge)", "welcome_badge")}
        </div>
        <div className="mt-4">
          {fld("النص التعريفي للموقع", "hero_description", true)}
        </div>
      </Card>

      <Card title="التذييل والتواصل">
        {fld("نص التعريف في تذييل الموقع", "footer_about", true)}
        <div className="grid md:grid-cols-2 gap-4 mt-4">
          {fld("البريد الإلكتروني", "contact_email", false, "email")}
        </div>
      </Card>

      <div className="sticky bottom-4 z-10 flex justify-end">
        <Btn color="gold" onClick={save} disabled={saving} data-testid="settings-save">
          {saving ? <><Loader2 className="w-4 h-4 inline animate-spin ms-1" /> جارٍ الحفظ...</> : <><Check className="w-4 h-4 inline ms-1" /> حفظ التغييرات</>}
        </Btn>
      </div>
    </motion.div>
  );
};

const FilesManager = ({ files, indicators, domains, levels, refresh, onView }) => {
  const fileRef = useRef(null);
  const [title, setTitle] = useState(""); const [desc, setDesc] = useState("");
  const [indicatorId, setIndicatorId] = useState("");
  const [pdf, setPdf] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [dragging, setDragging] = useState(false);

  const upload = async () => {
    if (!title.trim() || !indicatorId || !pdf) return toast.error("العنوان والمؤشر والملف مطلوبة");
    const fd = new FormData();
    fd.append("title", title); fd.append("description", desc); fd.append("indicator_id", indicatorId); fd.append("file", pdf);
    setUploading(true);
    try {
      await api.post("/files", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("تم رفع الملف"); setTitle(""); setDesc(""); setPdf(null);
      if (fileRef.current) fileRef.current.value = "";
      refresh();
    } catch (e) { toast.error(e.response?.data?.detail || "فشل الرفع"); }
    finally { setUploading(false); }
  };

  const del = async (id) => {
    if (!window.confirm("حذف الملف؟")) return;
    await api.delete(`/files/${id}`); toast.success("تم الحذف"); refresh();
  };

  return (
    <Card title="إدارة الملفات">
      <div className="grid lg:grid-cols-[1fr_auto] gap-4 mb-6">
        <div className="grid md:grid-cols-2 gap-3">
          <input data-testid="file-title-input" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="عنوان الملف *" className="px-4 py-2.5 rounded-lg border border-slate-200 text-sm" />
          <select data-testid="file-indicator-select" value={indicatorId} onChange={(e) => setIndicatorId(e.target.value)} className="px-4 py-2.5 rounded-lg border border-slate-200 text-sm bg-white">
            <option value="">اختر المؤشر *</option>
            {indicators.map((i) => {
              const dom = domains.find((d) => d.id === i.domain_id);
              const lvl = dom ? levels.find((l) => l.id === dom.level_id) : null;
              return <option key={i.id} value={i.id}>{lvl?.name} › {dom?.name} › {i.name}</option>;
            })}
          </select>
          <input data-testid="file-desc-input" value={desc} onChange={(e) => setDesc(e.target.value)} placeholder="وصف مختصر (اختياري)" className="px-4 py-2.5 rounded-lg border border-slate-200 text-sm md:col-span-2" />
        </div>
      </div>
      <label
        onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={(e) => { e.preventDefault(); setDragging(false); const f = e.dataTransfer.files[0]; if (f && f.type === "application/pdf") setPdf(f); else toast.error("ملف PDF فقط"); }}
        data-testid="dropzone"
        className={`block border-2 border-dashed rounded-2xl p-10 text-center cursor-pointer transition-colors ${dragging ? "border-gold bg-gold/5" : "border-brand/20 bg-brand/3 hover:bg-brand/5"}`}
      >
        <input ref={fileRef} type="file" accept="application/pdf" hidden data-testid="file-input" onChange={(e) => setPdf(e.target.files[0])} />
        <Upload className="w-10 h-10 text-brand mx-auto mb-3" />
        <div className="font-bold text-brand-dark mb-1">اسحب وأفلت ملف PDF هنا</div>
        <div className="text-xs text-slate-500 mb-3">أو اضغط للاختيار من جهازك</div>
        {pdf && <div className="text-sm text-emerald-700 font-bold flex items-center justify-center gap-2"><Check className="w-4 h-4" /> {pdf.name}</div>}
      </label>
      <div className="mt-4 flex justify-end">
        <Btn color="gold" onClick={upload} disabled={uploading} data-testid="upload-submit">
          {uploading ? <><Loader2 className="w-4 h-4 inline animate-spin ms-1" /> جارٍ الرفع...</> : <><Upload className="w-4 h-4 inline ms-1" /> رفع الملف</>}
        </Btn>
      </div>

      <div className="mt-8">
        <h4 className="font-heading font-bold text-brand-dark mb-3">الملفات المرفوعة ({files.length})</h4>
        <div className="grid md:grid-cols-2 gap-3 max-h-[500px] overflow-y-auto">
          {files.map((f) => {
            const ind = indicators.find((i) => i.id === f.indicator_id);
            return (
              <div key={f.id} data-testid={`admin-file-${f.id}`} className="flex items-center gap-3 p-3 rounded-xl border border-slate-100 hover:border-brand/30 transition-colors">
                <div className="w-12 h-12 rounded-lg bg-red-50 flex items-center justify-center shrink-0">
                  <FileText className="w-6 h-6 text-red-500" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-bold text-brand-dark text-sm truncate">{f.title}</div>
                  <div className="text-xs text-slate-500 truncate">{ind?.name}</div>
                </div>
                <div className="flex gap-1 shrink-0">
                  <Btn color="ghost" onClick={() => onView(f.id)} data-testid={`admin-view-${f.id}`}><Eye className="w-3.5 h-3.5" /></Btn>
                  <a href={`${API}/files/${f.id}/download`} target="_blank" rel="noreferrer" className="px-3 py-2 rounded-lg text-sm font-semibold bg-slate-100 text-slate-700 hover:bg-slate-200"><Download className="w-3.5 h-3.5" /></a>
                  <Btn color="red" onClick={() => del(f.id)} data-testid={`admin-del-${f.id}`}><Trash2 className="w-3.5 h-3.5" /></Btn>
                </div>
              </div>
            );
          })}
          {files.length === 0 && <div className="col-span-2 text-center py-8 text-slate-400 text-sm">لا توجد ملفات</div>}
        </div>
      </div>
    </Card>
  );
};

export default AdminDashboard;
